import { useRouter } from 'next/router';
import { useState, useEffect, useCallback } from 'react';

declare global {
  interface Window {
    adaSettings?: {
      adaReadyCallback?: () => void | Promise<void>;
    };
    adaEmbed?: {
      toggle: () => void;
      getInfo: () => Promise<{
        hasActiveChatter: boolean;
        hasClosedChat: boolean;
        isChatOpen: boolean;
        isDrawerOpen: boolean;
      }>;
    };
  }
}

interface UseAdaAutoOpenConfig {
  paths: string[];
  adaReady: boolean;
}

/**
 * A React hook that automatically opens Ada chat widget on specific paths and manages its state.
 *
 * This hook:
 * - Automatically opens Ada chat when navigating to specified paths
 * - Prevents auto-opening if user manually closed the chat
 * - Restores the previous chat state when leaving target paths
 * - Tracks manual close events to prevent unwanted re-opening
 * - Ensures chat only auto-opens once per session on target paths
 *
 * @param config - Configuration object
 * @param config.paths - Array of URL paths where Ada chat should auto-open
 * @param config.adaReady - Boolean indicating if Ada embed is loaded and ready
 *
 * @example
 * ```tsx
 * // Auto-open Ada chat on checkout and support pages
 * useAdaAutoOpen({
 *   paths: ['/checkout', '/support'],
 *   adaReady: isAdaLoaded
 * });
 * ```
 */
const useAdaAutoOpen = ({ paths, adaReady }: UseAdaAutoOpenConfig) => {
  const [wasManuallyClosed, setWasManuallyClosed] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [previousState, setPreviousState] = useState<boolean | null>(null);
  const { pathname } = useRouter();

  const isTargetPath = paths.includes(pathname);

  const restorePreviousState = useCallback(async () => {
    if (!window.adaEmbed || previousState === null) return;

    try {
      const info = await window.adaEmbed.getInfo();

      if (info.isChatOpen !== previousState) {
        window.adaEmbed.toggle();
      }
    } catch (error) {
      console.warn('Failed to restore Ada chat state:', error);
    }
  }, [previousState]);

  const checkAndToggleAda = useCallback(async () => {
    if (isTargetPath && adaReady && window.adaEmbed && !wasManuallyClosed && !hasAutoOpened) {
      try {
        const info = await window.adaEmbed.getInfo();

        // Store the current state before we potentially change it
        if (previousState === null) {
          setPreviousState(info.isChatOpen);
        }

        if (!info.isChatOpen) {
          window.adaEmbed.toggle();
        }

        setHasAutoOpened(true);
      } catch (error) {
        console.warn('Failed to get Ada chat info:', error);
      }
    }
  }, [isTargetPath, adaReady, wasManuallyClosed, hasAutoOpened, previousState]);

  const resetStates = useCallback(() => {
    setPreviousState(null);
    setWasManuallyClosed(false);
    setHasAutoOpened(false);
  }, []);

  // Restore previous state when leaving target paths
  useEffect(() => {
    if (!isTargetPath) {
      if (previousState !== null && adaReady && window.adaEmbed) {
        restorePreviousState().finally(resetStates);
      } else {
        resetStates();
      }
    }
  }, [isTargetPath, adaReady, previousState, restorePreviousState, resetStates]);

  // Track manual close events
  useEffect(() => {
    if (!adaReady || !window.adaEmbed) return;

    const handleAdaClose = () => {
      if (isTargetPath && hasAutoOpened) {
        setWasManuallyClosed(true);
      }
    };

    window.addEventListener('ada:chat_closed', handleAdaClose);

    return () => {
      window.removeEventListener('ada:chat_closed', handleAdaClose);
    };
  }, [adaReady, isTargetPath, hasAutoOpened]);

  // Listen for route changes
  useEffect(() => {
    checkAndToggleAda();
  }, [checkAndToggleAda]);
};

export default useAdaAutoOpen;
